import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/** Lazily loads a named page export so Vite can emit one chunk per route. */
function lazyPage<P extends Record<string, ComponentType<unknown>>>(
  factory: () => Promise<P>,
  exportName: keyof P & string,
): LazyExoticComponent<ComponentType<unknown>> {
  return lazy(async () => {
    const module = await factory();
    return { default: module[exportName] as ComponentType<unknown> };
  });
}

export const LoginPage = lazyPage(() => import("@/features/auth/LoginPage.js"), "LoginPage");
export const SignupPage = lazyPage(() => import("@/features/auth/SignupPage.js"), "SignupPage");
export const ForgotPasswordPage = lazyPage(
  () => import("@/features/auth/ForgotPasswordPage.js"),
  "ForgotPasswordPage",
);
export const ResetPasswordPage = lazyPage(
  () => import("@/features/auth/ResetPasswordPage.js"),
  "ResetPasswordPage",
);
export const AcceptInvitePage = lazyPage(
  () => import("@/features/auth/AcceptInvitePage.js"),
  "AcceptInvitePage",
);
export const CampaignListPage = lazyPage(
  () => import("@/features/campaigns/CampaignListPage.js"),
  "CampaignListPage",
);
export const CampaignWizardLayout = lazyPage(
  () => import("@/features/campaigns/CampaignWizardLayout.js"),
  "CampaignWizardLayout",
);
export const CampaignContactsPage = lazyPage(
  () => import("@/features/campaigns/CampaignContactsPage.js"),
  "CampaignContactsPage",
);
export const SurveyBuilderPage = lazyPage(
  () => import("@/features/survey/SurveyBuilderPage.js"),
  "SurveyBuilderPage",
);
export const QuotaEditorPage = lazyPage(
  () => import("@/features/quota/QuotaEditorPage.js"),
  "QuotaEditorPage",
);
export const AgentConfigPage = lazyPage(
  () => import("@/features/agent/AgentConfigPage.js"),
  "AgentConfigPage",
);
export const RunListPage = lazyPage(() => import("@/features/runs/RunListPage.js"), "RunListPage");
export const RunDetailPage = lazyPage(
  () => import("@/features/runs/RunDetailPage.js"),
  "RunDetailPage",
);
export const RunResultsPage = lazyPage(
  () => import("@/features/runs/RunResultsPage.js"),
  "RunResultsPage",
);
export const ContactSetListPage = lazyPage(
  () => import("@/features/audience/ContactSetListPage.js"),
  "ContactSetListPage",
);
export const ContactSetDetailPage = lazyPage(
  () => import("@/features/audience/ContactSetDetailPage.js"),
  "ContactSetDetailPage",
);
export const GenerateSamplePage = lazyPage(
  () => import("@/features/audience/GenerateSamplePage.js"),
  "GenerateSamplePage",
);
export const SettingsLayout = lazyPage(
  () => import("@/features/settings/SettingsLayout.js"),
  "SettingsLayout",
);
export const ProfilePage = lazyPage(
  () => import("@/features/settings/ProfilePage.js"),
  "ProfilePage",
);
export const OrganizationPage = lazyPage(
  () => import("@/features/settings/OrganizationPage.js"),
  "OrganizationPage",
);
export const MembersPage = lazyPage(
  () => import("@/features/settings/MembersPage.js"),
  "MembersPage",
);
export const ApiKeysPage = lazyPage(
  () => import("@/features/settings/ApiKeysPage.js"),
  "ApiKeysPage",
);
export const NumberingAdminPage = lazyPage(
  () => import("@/features/settings/NumberingAdminPage.js"),
  "NumberingAdminPage",
);
export const AuthenticatedShell = lazyPage(
  () => import("@/routes/authenticated-shell.js"),
  "AuthenticatedShell",
);
